// ============================================================================
// ReportHeader — the report's masthead: client identity, the reporting-period
// selector (month / quarter / all / year) and the Download PDF action.
// ----------------------------------------------------------------------------
// The PDF action hits the EXISTING server-rendered route
// `/clients/{id}/report.pdf?period=…` (same-origin; not under /api — that route
// lives on the HTML app and already falls back to inline HTML on the Windows
// dev box). We open it in a new tab so the in-app SPA state is never lost.
// ============================================================================
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Avatar, Badge, Button, SelectField } from "../../components";
import { REPORT_PERIODS } from "./types";
import type { ReportClient, ReportPeriod } from "./types";
import { fmtDate } from "./format";

/** Build the same-origin PDF URL for the existing server route. */
export function pdfHref(clientId: number, period: ReportPeriod): string {
  const qs = new URLSearchParams({ period }).toString();
  return `/clients/${clientId}/report.pdf?${qs}`;
}

function PdfIcon() {
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="none" aria-hidden>
      <path
        d="M12 3v10m0 0 4-4m-4 4-4-4"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 17v2a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export interface ReportHeaderProps {
  client: ReportClient;
  period: ReportPeriod;
  onPeriodChange: (p: ReportPeriod) => void;
}

export function ReportHeader({
  client,
  period,
  onPeriodChange,
}: ReportHeaderProps) {
  const { t, i18n } = useTranslation("clientReport");
  const reduce = useReducedMotion();
  const locale = i18n.language;
  const today = fmtDate(new Date().toISOString(), locale);

  return (
    <motion.header
      initial={reduce ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
      className="overflow-hidden rounded-xl bg-ink-800 text-ondark shadow-md ring-1 ring-ink-line"
    >
      {/* Brand gradient wash strip */}
      <div className="h-1.5 w-full bg-grad-hero" aria-hidden />

      <div className="flex flex-col gap-6 p-6 sm:p-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <Avatar name={client.name} size="xl" />
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-kicker text-volt-400">
                {t("kicker")}
              </p>
              <h1 className="mt-1 break-words font-display text-3xl font-extrabold leading-none tracking-tightest sm:text-4xl">
                {client.name}
              </h1>
              {client.occupation && (
                <p className="mt-1.5 text-sm text-ondark-muted">
                  {client.occupation}
                </p>
              )}
              <p className="mt-2 text-xs text-ondark-muted">
                {t("preparedOn", { date: today })}
              </p>
            </div>
          </div>

          <Badge tone="volt" solid className="self-start">
            {t(`period.${period}`)}
          </Badge>
        </div>

        {/* Controls: period selector + Download PDF */}
        <div className="flex flex-col gap-3 border-t border-ink-line/60 pt-5 sm:flex-row sm:items-end sm:justify-between print:hidden">
          <div className="w-full sm:w-56">
            <label
              htmlFor="report-period"
              className="mb-1.5 block font-display text-xs font-bold uppercase tracking-kicker text-ondark-muted"
            >
              {t("period.label")}
            </label>
            <SelectField
              id="report-period"
              label={t("period.label")}
              hideLabel
              value={period}
              onChange={(e) => onPeriodChange(e.target.value as ReportPeriod)}
              options={REPORT_PERIODS.map((p) => ({
                value: p,
                label: t(`period.${p}`),
              }))}
            />
          </div>

          <a
            href={pdfHref(client.id, period)}
            target="_blank"
            rel="noopener noreferrer"
            className="self-start sm:self-auto"
          >
            <Button size="md" iconLeft={<PdfIcon />}>
              {t("actions.downloadPdf")}
            </Button>
          </a>
        </div>
      </div>
    </motion.header>
  );
}
